<template>
  <div class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
    <div class="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
      <!-- Header -->
      <div class="flex justify-between items-center mb-6">
        <h3 class="text-lg font-medium text-gray-900">Lead Details</h3>
        <button @click="$emit('close')" class="text-gray-400 hover:text-gray-600">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>

      <!-- Lead Information -->
      <div class="space-y-6">
        <!-- Basic Information -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Name</label>
            <input
              v-model="editedLead.name"
              type="text"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Email</label>
            <input
              v-model="editedLead.email"
              type="email"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Company</label>
            <input
              v-model="editedLead.company"
              type="text"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Interest</label>
            <select
              v-model="editedLead.interest"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Select Interest</option>
              <option value="Product Demo">Product Demo</option>
              <option value="Pricing">Pricing</option>
              <option value="Partnership">Partnership</option>
              <option value="General Inquiry">General Inquiry</option>
              <option value="Other">Other</option>
            </select>
          </div>
        </div>

        <!-- Show Information -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Show</label>
          <div class="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md">
            <div class="text-sm text-gray-900">{{ lead.show_name }}</div>
            <div class="text-xs text-gray-500">
              {{ formatDate(lead.show_start_date) }} - {{ formatDate(lead.show_end_date) }}
            </div>
          </div>
        </div>

        <!-- Follow-up Status -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Follow-up Status</label>
          <div class="flex items-center space-x-4">
            <label class="flex items-center">
              <input
                v-model="editedLead.is_followed_up"
                type="checkbox"
                class="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span class="ml-2 text-sm text-gray-700">Mark as followed up</span>
            </label>
            <span
              :class="[
                'inline-flex px-2 py-1 text-xs font-semibold rounded-full',
                editedLead.is_followed_up
                  ? 'bg-green-100 text-green-800'
                  : 'bg-gray-100 text-gray-800'
              ]"
            >
              {{ editedLead.is_followed_up ? 'Followed Up' : 'Not Followed Up' }}
            </span>
          </div>
        </div>

        <!-- Notes -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Notes</label>
          <textarea
            v-model="editedLead.notes"
            rows="4"
            placeholder="Add notes about this lead..."
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          ></textarea>
        </div>

        <!-- Lead Information -->
        <div class="bg-gray-50 rounded-lg p-4">
          <h4 class="text-sm font-medium text-gray-700 mb-3">Lead Information</h4>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <span class="text-gray-500">Created:</span>
              <span class="ml-2 text-gray-900">{{ formatDateTime(lead.created_at) }}</span>
            </div>
            <div>
              <span class="text-gray-500">Lead ID:</span>
              <span class="ml-2 text-gray-900">#{{ lead.id }}</span>
            </div>
            <div v-if="lead.synced_at">
              <span class="text-gray-500">Synced:</span>
              <span class="ml-2 text-gray-900">{{ formatDateTime(lead.synced_at) }}</span>
            </div>
          </div>
        </div>

        <!-- Lead Capture URL -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Lead Capture URL</label>
          <div class="flex">
            <input
              :value="leadCaptureUrl"
              type="text"
              readonly
              class="flex-1 px-3 py-2 border border-gray-300 rounded-l-md bg-gray-50 text-gray-500"
            />
            <button
              @click="copyUrl"
              class="px-4 py-2 bg-gray-600 text-white rounded-r-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              Copy
            </button>
          </div>
        </div>
      </div>

      <!-- Actions -->
      <div class="flex justify-end space-x-3 mt-8 pt-6 border-t border-gray-200">
        <button
          @click="$emit('close')"
          class="btn-secondary"
        >
          Cancel
        </button>
        <button
          @click="saveChanges"
          :disabled="saving"
          class="btn-primary"
        >
          <div v-if="saving" class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
          {{ saving ? 'Saving...' : 'Save Changes' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import config from '../config.js'

// Props
const props = defineProps({
  lead: {
    type: Object,
    required: true
  }
})

// Emits
const emit = defineEmits(['close', 'update'])

// Reactive data
const saving = ref(false)
const editedLead = ref({})

// Computed properties
const leadCaptureUrl = computed(() => {
  if (props.lead.show_slug && props.lead.client_slug) {
    const baseUrl = window.location.origin
    return `${baseUrl}/booth/${props.lead.client_slug}/${props.lead.show_slug}`
  }
  return ''
})

// Methods
const saveChanges = async () => {
  try {
    saving.value = true
    const token = localStorage.getItem('admin_token')
    if (!token) return

    // Update lead information
    const response = await fetch(`${config.apiBaseUrl}/admin/leads/${props.lead.id}/follow-up`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        is_followed_up: editedLead.value.is_followed_up,
        name: editedLead.value.name,
        email: editedLead.value.email,
        company: editedLead.value.company,
        interest: editedLead.value.interest,
        notes: editedLead.value.notes
      })
    })

    if (response.ok) {
      emit('update')
      emit('close')
    } else {
      const error = await response.json()
      alert(`Error saving changes: ${error.error || 'Unknown error'}`)
    }
  } catch (error) {
    console.error('Error saving lead:', error)
    alert('Error saving changes. Please try again.')
  } finally {
    saving.value = false
  }
}

const copyUrl = async () => {
  try {
    await navigator.clipboard.writeText(leadCaptureUrl.value)
    // You could add a toast notification here
  } catch (error) {
    console.error('Error copying URL:', error)
    // Fallback for older browsers
    const textArea = document.createElement('textarea')
    textArea.value = leadCaptureUrl.value
    document.body.appendChild(textArea)
    textArea.select()
    document.execCommand('copy')
    document.body.removeChild(textArea)
  }
}

const formatDate = (dateString) => {
  if (!dateString) return ''
  return new Date(dateString).toLocaleDateString()
}

const formatDateTime = (dateString) => {
  if (!dateString) return ''
  return new Date(dateString).toLocaleString()
}

// Initialize edited lead data
onMounted(() => {
  editedLead.value = {
    name: props.lead.name || '',
    email: props.lead.email || '',
    company: props.lead.company || '',
    interest: props.lead.interest || '',
    notes: props.lead.notes || '',
    is_followed_up: props.lead.is_followed_up || false
  }
})
</script> 